import { useState, useRef, useEffect } from 'react';

import type { PublicUser } from '@smartapply/shared';

interface UserMenuProps {
  user: PublicUser;
  onLogout: () => void | Promise<void>;
}

export function UserMenu({ user, onLogout }: UserMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', onClickOutside);
      document.addEventListener('keydown', onEscape);
    }
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEscape);
    };
  }, [open]);

  const initials = user.displayName
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-sm border border-[var(--border-default)] bg-[var(--bg-paper-elevated)] px-2.5 py-1.5 font-sans text-sm text-[var(--ink-primary)] hover:border-[var(--border-strong)]"
        aria-haspopup="true"
        aria-expanded={open}
      >
        <span
          aria-hidden
          className="flex h-6 w-6 items-center justify-center rounded-sm font-mono text-[10px] font-semibold uppercase tracking-wider"
          style={{
            backgroundColor: 'var(--accent-saffron-bg)',
            color: 'var(--accent-saffron)',
            border: '1px solid var(--accent-saffron-border)',
          }}
        >
          {initials || '·'}
        </span>
        <span className="hidden sm:inline">{user.displayName}</span>
        <span aria-hidden className="text-[var(--ink-tertiary)]">▾</span>
      </button>

      {open && (
        <div
          role="menu"
          className="card-paper-strong absolute right-0 top-full z-20 mt-2 w-72 rounded-sm"
        >
          <div className="border-b border-[var(--border-subtle)] px-4 py-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
              logado como
            </p>
            <p className="mt-1 font-serif text-sm text-[var(--ink-primary)]">
              {user.displayName}
            </p>
            <p className="font-mono text-xs text-[var(--ink-tertiary)]">{user.email}</p>
            {!user.emailVerified && (
              <p className="mt-2 font-sans text-[11px] text-[var(--accent-saffron)]">
                ⚠ E-mail ainda não confirmado
              </p>
            )}
          </div>

          <ul className="py-1">
            <li>
              <a
                href="#/seguranca"
                onClick={() => setOpen(false)}
                className="block w-full px-4 py-2 text-left font-sans text-sm text-[var(--ink-primary)] hover:bg-[var(--bg-paper-recessed)]"
              >
                Segurança e 2FA
              </a>
            </li>
            {user.isAdmin && (
              <li>
                <a
                  href="#/admin/sugestoes"
                  onClick={() => setOpen(false)}
                  className="flex w-full items-baseline justify-between px-4 py-2 text-left font-sans text-sm text-[var(--ink-primary)] hover:bg-[var(--bg-paper-recessed)]"
                >
                  <span>Sugestões (admin)</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--accent-saffron)]">
                    admin
                  </span>
                </a>
              </li>
            )}
            <li>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  void onLogout();
                }}
                className="block w-full px-4 py-2 text-left font-sans text-sm text-[var(--ink-primary)] hover:bg-[var(--bg-paper-recessed)]"
              >
                Sair
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}
